import React, { useState } from 'react';
import styled from 'styled-components';
import toast from 'react-hot-toast';
import { useAuth } from '../context/AuthContext';
import { useMerchant } from '../context/MerchantContext';
import { customerAuthApi } from '../services/api';
import FormInput from './common/FormInput';
import FormContainer from './common/FormContainer';
import BottomSheet from './common/BottomSheet';

const Subtitle = styled.p`
  text-align: center;
  margin: 0 0 1.5rem;
  color: #718096;
  font-size: 0.9rem;
`;

const Button = styled.button`
  padding: 1rem;
  background-color: ${props => props.theme.primary || '#e74c3c'};
  color: white;
  border: none;
  border-radius: 12px;
  font-size: 1rem;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.2s;
  margin-top: 1rem;
  box-shadow: 0 4px 6px ${props => props.theme.primary ? `${props.theme.primary}33` : 'rgba(231, 76, 60, 0.2)'};

  &:hover {
    filter: brightness(0.9);
    transform: translateY(-1px);
  }

  &:disabled {
    background-color: ${props => props.theme.primary ? `${props.theme.primary}80` : '#feb2b2'};
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }
`;

const LinkButton = styled.button`
  background: none;
  border: none;
  color: ${props => props.theme.primary || '#e74c3c'};
  cursor: pointer;
  font-weight: 600;
  font-size: 0.9rem;
  transition: color 0.2s;

  &:hover {
    filter: brightness(0.9);
    text-decoration: underline;
  }
`;

const BottomRow = styled.div`
  text-align: center;
  margin-top: 1.5rem;
`;

const EyeButton = styled.button`
  background: none;
  border: none;
  color: #a0aec0;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0.25rem;
`;

const ForgotPasswordModal: React.FC = () => {
  const { showForgotPasswordModal, setShowForgotPasswordModal, setShowLoginModal, login } = useAuth();
  const { merchantId } = useMerchant();

  const [step, setStep] = useState<'request' | 'confirm'>('request');
  const [cpfOrPhone, setCpfOrPhone] = useState('');
  const [code, setCode] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  if (!showForgotPasswordModal) return null;

  const reset = () => {
    setStep('request');
    setCpfOrPhone('');
    setCode('');
    setPassword('');
    setShowPassword(false);
    setIsLoading(false);
  };

  const handleClose = () => {
    reset();
    setShowForgotPasswordModal(false);
  };

  const handleRequest = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      // merchantId identifica a loja (instância de WhatsApp) que enviará o código.
      await customerAuthApi.forgotPassword({ cpfOrPhone, merchantId: merchantId || undefined });
    } catch (error) {
      // Resposta genérica (anti-enumeração): seguimos para o passo do código de qualquer forma.
    } finally {
      toast.success('Se houver uma conta, enviamos um código por WhatsApp.');
      setStep('confirm');
      setIsLoading(false);
    }
  };

  const handleConfirm = async (e: React.FormEvent) => {
    e.preventDefault();
    if (password.length < 8 || !/[a-zA-Z]/.test(password) || !/[0-9]/.test(password)) {
      toast.error('A senha deve ter ao menos 8 caracteres, com pelo menos 1 letra e 1 número.');
      return;
    }
    setIsLoading(true);
    try {
      await customerAuthApi.resetPassword({ cpfOrPhone, code, password });
      // Estabelece a sessão pelo fluxo padrão de login (persiste tokens no AuthContext).
      const success = await login({ cpfOrPhone, password });
      if (success) {
        toast.success('Senha redefinida com sucesso!');
        handleClose();
      } else {
        toast.success('Senha redefinida! Faça login para continuar.');
        handleClose();
        setShowLoginModal(true);
      }
    } catch (error) {
      toast.error('Código inválido ou expirado. Verifique e tente novamente.');
    } finally {
      setIsLoading(false);
    }
  };

  const backToLogin = () => {
    handleClose();
    setShowLoginModal(true);
  };

  return (
    <BottomSheet
      isOpen={showForgotPasswordModal}
      onClose={handleClose}
      maxWidth="450px"
      zIndex={10500}
      title="Esqueci minha senha"
    >
      <div style={{ padding: '1.5rem 2rem 2rem' }}>
        <Subtitle>
          {step === 'request'
            ? 'Informe seu celular ou CPF para receber um código de recuperação.'
            : 'Digite o código recebido e defina sua nova senha.'}
        </Subtitle>

        {step === 'request' ? (
          <FormContainer as="form" onSubmit={handleRequest}>
            <FormInput
              label="Celular ou CPF"
              type="text"
              placeholder="Digite seu celular ou CPF"
              value={cpfOrPhone}
              onChange={(e) => setCpfOrPhone(e.target.value)}
              disabled={isLoading}
              required
            />
            <Button type="submit" disabled={isLoading}>
              {isLoading ? 'Enviando...' : 'Enviar código'}
            </Button>
          </FormContainer>
        ) : (
          <FormContainer as="form" onSubmit={handleConfirm}>
            <FormInput
              label="Código recebido"
              type="text"
              inputMode="numeric"
              placeholder="Digite o código de 6 dígitos"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              disabled={isLoading}
              required
            />
            <FormInput
              label="Nova senha"
              type={showPassword ? 'text' : 'password'}
              placeholder="Mínimo 8 caracteres, com letra e número"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              disabled={isLoading}
              required
              rightElement={
                <EyeButton type="button" onClick={() => setShowPassword(!showPassword)}>
                  <i className={`fas ${showPassword ? 'fa-eye-slash' : 'fa-eye'}`}></i>
                </EyeButton>
              }
            />
            <Button type="submit" disabled={isLoading}>
              {isLoading ? 'Redefinindo...' : 'Redefinir senha e entrar'}
            </Button>
            <BottomRow>
              <LinkButton type="button" onClick={() => setStep('request')}>
                Não recebeu? Reenviar código
              </LinkButton>
            </BottomRow>
          </FormContainer>
        )}

        <BottomRow>
          <LinkButton type="button" onClick={backToLogin}>
            Lembrou a senha? Voltar para o login
          </LinkButton>
        </BottomRow>
      </div>
    </BottomSheet>
  );
};

export default ForgotPasswordModal;
